import React, { useRef, useState } from 'react';
import { Grid2 as Grid, Stack, Box, Typography } from '@mui/material';
import { FormattedTextField } from 'framework/ui/field';
import { Show } from 'framework/show';
import IconButton from '@mui/material/IconButton';
import DownloadIcon from '@mui/icons-material/Download';
import { Tooltip, Button, Loading } from 'framework/ui/mui-builder';
import { DATE_FORMAT, useDateService } from 'framework/utils';
import { useMessageService, ErrorMessage, SuccessMessage } from 'framework/ui/Message';
import EVMDataCharts from './EVMDataCharts';
import ProjectInfo from './ProjectInfo';
import ProjectBudget from './ProjectBudget';
import ProjectTimeline from './ProjectTimeline';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  useProjectDataContext,
  useProjectSecurityContext,
} from 'domain/shared/newProject';
import useEVMService from './useEVMService';
import { useProjectEVM } from './hooks/useEVM';

const EVMLayout = () => {
  const { uploadEVMFile, downloadEVMFile } = useEVMService();
  const { data } = useProjectDataContext();
  const { isAccessGranted } = useProjectSecurityContext();
  const { evm, loading: dataLoading, refetch } = useProjectEVM(data.id!);
  const { formatDate } = useDateService();
  const { addMessage } = useMessageService();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleCancelFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpdateData = async () => {
    if (!selectedFile) {
      fileInputRef.current?.click();
      return;
    }

    try {
      setUploading(true);
      await uploadEVMFile(data.id!, selectedFile);

      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      refetch();
      addMessage(new SuccessMessage('File uploaded successfully'));
    } catch (error) {
      addMessage(new ErrorMessage('Error uploading file: Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadData = async () => {
    try {
      setLoading(true);
      await downloadEVMFile(data.id!);

      addMessage(new SuccessMessage('File downloaded successfully'));
    } catch (error) {
      addMessage(new ErrorMessage('Error downloading file: Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = () => {
    if (uploading) return 'Uploading...';
    if (selectedFile) return 'Upload file';
    return 'Update data';
  };

  if (dataLoading) {
    return <Loading isInitialPosition />;
  }

  return (
    <Show getDataMethod={() => Promise.resolve(evm)}>
      <Stack spacing={3}>
        <Grid container spacing={4} alignItems="start" size={12}>
          <ProjectInfo />
          <ProjectBudget />
          <ProjectTimeline />
        </Grid>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
          <FormattedTextField
            label=""
            format={(str: string) => (
              <Typography variant="body2" color="textSecondary" pb={1}>
                Last updated: {formatDate(str, DATE_FORMAT.FULL_DATE)}
              </Typography>
            )}
            source="lastUpdate"
          />

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept=".xlsx,.xls,.csv"
          />

          {selectedFile && (
            <Typography variant="body1" mb={1}>
              Selected file: {selectedFile.name}
            </Typography>
          )}

          <Stack direction="row" spacing={2}>
            {isAccessGranted(ACCESS_INFO_SECTIONS.EVM, ACCESS_INFO_ACTIONS.MANAGE) && (
              <>
                <Button variant="contained" onClick={handleUpdateData} disabled={uploading}>
                  {getButtonText()}
                </Button>

                {selectedFile && !uploading && (
                  <Button variant="outlined" onClick={handleCancelFile}>
                    Cancel
                  </Button>
                )}
              </>
            )}

            {isAccessGranted(ACCESS_INFO_SECTIONS.EVM, ACCESS_INFO_ACTIONS.MANAGE) && !selectedFile && (
              <IconButton onClick={handleDownloadData} disabled={loading || uploading}>
                <Tooltip title="Download last file" placement="right">
                  <DownloadIcon />
                </Tooltip>
              </IconButton>
            )}
          </Stack>
        </Box>

        <EVMDataCharts />
      </Stack>
    </Show>
  );
};

export default EVMLayout;
